import { useEffect, useMemo } from 'react'
import * as React from 'react'
import { throttle } from 'lodash'
import { type EditorView } from 'prosemirror-view'

import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { isPlatformMobileIOS } from 'common/Prose/utils/platformSniffing'
import { HIDDEN_INPUT_ID, MAIN_CONTENT_ID } from 'screens/ProseEdit/Constants'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { LINK_MODAL_ID } from 'common/LinkModal/constants'
import { isKeyboardOverlappingContent, keyboardHeight } from 'utils/virtualKeyboardOverlap'

import { useMobileToolbar } from '../stores/MobileToolbarStore'
import { TopTiers } from '../Types'

const NAV_BAR_HEIGHT = 49
const BASE_TIER_HEIGHT = 48
const SECOND_TIER_HEIGHT = 48 + 1 /* border */
const MOBILE_TOOLBAR_BOTTOM_HEIGHT = NAV_BAR_HEIGHT + BASE_TIER_HEIGHT + SECOND_TIER_HEIGHT

function windowHeight() {
  return window.visualViewport?.height ?? window.innerHeight
}

function topMinusElementHeight(mobileToolbarHeight?: number) {
  return windowHeight() - (mobileToolbarHeight ?? 0) + Math.max(0, window.scrollY) - keyboardHeight()
}

function bottomMinusElementHeight() {
  return window.innerHeight - windowHeight() + keyboardHeight()
}

function maxTieredDialogHeight() {
  return windowHeight() - MOBILE_TOOLBAR_BOTTOM_HEIGHT + Math.max(0, window.scrollY)
}

/**
 * Basic mobile probing and iOS specific effects.
 * Only applies to iOS: This sets up global listeners in an attempt to position the mobile toolbar at the bottom of the visible area.
 */
export function useMobileToolbarPositionHacks({
  mobileToolbarRef,
  collaborationDialogRef,
}: {
  mobileToolbarRef: React.RefObject<HTMLDivElement>
  collaborationDialogRef: React.RefObject<HTMLDivElement>
}) {
  const hasVirtualKeyboard = 'virtualKeyboard' in navigator
  const { isMobile } = useResponsiveDesign()
  const isMobileIOS = isPlatformMobileIOS()
  const editorView = useEditorView()
  const mobileToolbarStore = useMobileToolbar()
  let MOBILE_TOOLBAR_TOP = Infinity

  // iOS Safari has no plans to support this yet, other browsers don't need it
  // I cannot test so I prefer to keep it commented (X hours wasted kind of warn)
  if (hasVirtualKeyboard) {
    ;(navigator as any).virtualKeyboard.overlaysContent = true
  }

  // [REPOSITION TOOLBAR / RESIZE MAIN] (keep this fast)
  const moveMobileToolbar = useMemo(
    () =>
      throttle(
        () => {
          if (!isMobile || !mobileToolbarRef.current) return
          const isComments = mobileToolbarStore.topTier === TopTiers.Comments
          const keyboardCoversContent = isKeyboardOverlappingContent()
          const bottom = Math.round(bottomMinusElementHeight())
          const intendedBottom = `${bottom}px`
          if (
            mobileToolbarRef.current.style.bottom !== intendedBottom &&
            (isKeyboardOverlappingContent() || mobileToolbarRef.current.style.bottom)
          ) {
            // Since Chrome 108 we started to need this for Android
            mobileToolbarRef.current.style.bottom = intendedBottom
          }
          if (mobileToolbarStore.appleBottom !== bottom) {
            mobileToolbarStore.setAppleBottom(bottom)
          }
          if (mobileToolbarStore.keyboardCoversContent !== keyboardCoversContent) {
            mobileToolbarStore.keyboardCoversContent = keyboardCoversContent
          }

          let mobileToolbarHeight = mobileToolbarRef.current.offsetHeight
          if (isComments && collaborationDialogRef.current != null) {
            mobileToolbarHeight = collaborationDialogRef.current.offsetHeight
          }

          const top = topMinusElementHeight(mobileToolbarHeight)
          if (MOBILE_TOOLBAR_TOP !== top) {
            const isUnsquishing = MOBILE_TOOLBAR_TOP < 100
            const isReducingInSize = top < MOBILE_TOOLBAR_TOP
            // eslint-disable-next-line react-hooks/exhaustive-deps
            MOBILE_TOOLBAR_TOP = top
            // Don't bother resizing main if you are going to squish it below readable
            if (top > 10) {
              const mainContent = document.getElementById(MAIN_CONTENT_ID)
              if (mainContent) {
                if (isReducingInSize || isUnsquishing) {
                  setTimeout(() => {
                    if (document.activeElement && !mainContent.contains(document.activeElement)) {
                      autoScrollToFocused()
                    } else {
                      adjustEditorScroll(mainContent, editorView)
                    }
                  }, 300)
                }
              }
            }
          }

          const headerHeight = 48
          const intendedHeight = `${Math.round(windowHeight() - keyboardHeight() - headerHeight)}px`
          // The link dialog resets everytime it is shown
          const linkDialog = document.getElementById(LINK_MODAL_ID) // TODO: We scan all the time, that sucks
          if (linkDialog && linkDialog.style.height !== intendedHeight) {
            linkDialog.style.transition = `height linear 200ms`
            // We want to cover the toolbar
            linkDialog.style.height = intendedHeight
          }
        },
        100,
        { leading: true, trailing: true },
      ),
    [isMobile, isMobileIOS, mobileToolbarRef, collaborationDialogRef, editorView],
  )

  // RESET MAIN OUTSIDE MOBILE
  useEffect(() => {
    return () => {
      const mainContent = document.getElementById(MAIN_CONTENT_ID)
      if (mainContent) {
        mainContent.style.transition = ``
        mainContent.style.maxHeight = ``
      }
    }
  }, [])

  // RESIZE
  useEffect(() => {
    if (!isMobile || window.visualViewport == null) return
    const visualViewport = window.visualViewport
    visualViewport.addEventListener('resize', moveMobileToolbar)
    return () => visualViewport.removeEventListener('resize', moveMobileToolbar)
  }, [isMobile, moveMobileToolbar])

  // INTERVAL POS
  useEffect(() => {
    if (!isMobile) return
    const interval = setInterval(moveMobileToolbar, 100)
    return () => clearInterval(interval)
  }, [isMobile, moveMobileToolbar])

  // SCROLL - MOBILE TOOLBAR
  useEffect(() => {
    // We don't need this on Android, the MT can be fixed bottom: 0
    if (!isMobileIOS) return
    window.addEventListener('scroll', moveMobileToolbar)
    return () => window.removeEventListener('scroll', moveMobileToolbar)
  }, [isMobileIOS, moveMobileToolbar])

  // [CALCULATE SIZE LOGIC] (very slow setProperty)
  const calculateMobileToolbarSize = useMemo(
    () =>
      throttle(
        () => {
          if (!isMobileIOS || !mobileToolbarRef.current) return
          mobileToolbarRef.current.style.setProperty(
            '--mobile-max-tiered-dialog-height',
            `${maxTieredDialogHeight()}px`,
          )
        },
        100,
        { leading: true, trailing: true },
      ),
    [isMobileIOS, mobileToolbarRef],
  )

  // INTERVAL SIZE
  useEffect(() => {
    if (!isMobileIOS) return
    const interval = setInterval(calculateMobileToolbarSize, 100)
    return () => clearInterval(interval)
  }, [isMobileIOS, calculateMobileToolbarSize])

  // [SCROLL - SCROLL BACK WINDOW]
  useEffect(() => {
    if (!isMobileIOS) return
    const scrollBack = throttle(
      () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      },
      100,
      { leading: true, trailing: true },
    )
    window.addEventListener('scroll', scrollBack)
    return () => window.removeEventListener('scroll', scrollBack)
  }, [isMobileIOS])

  // [SCROLL - SCROLL BACK WINDOW - FALLBACK FOR MISSED SCROLL EVENTS]
  useEffect(() => {
    if (!isMobileIOS) return
    const scrollBackIfNeeded = () => {
      if (window.scrollY > 0 || window.scrollX > 0) window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      if (window.document.body.scrollTop !== 0) window.document.body.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
    const interval = setInterval(scrollBackIfNeeded, 100)
    return () => clearInterval(interval)
  }, [isMobileIOS])

  return { hasVirtualKeyboard, isMobileIOS }
}

/**
 * Focus and other editor specific hacks
 */
export function useEditorFocusHacks() {
  const mobileToolbarStore = useMobileToolbar()
  const { isMobile } = useResponsiveDesign()
  const isMobileIOS = isPlatformMobileIOS()
  const editorView = useEditorView()

  // iOS Losing focus
  useEffect(() => {
    if (!isMobile || !editorView.dom) return
    const onKeyUp = refocusOnTheEditorIfNecessary({ focusedPm: editorView.dom, editorView })
    editorView.dom.addEventListener('keyup', onKeyUp)
    return () => editorView.dom.removeEventListener('keyup', onKeyUp)
  })

  // Hidden input hook up
  useEffect(() => {
    const hiddenInput = document.getElementById(HIDDEN_INPUT_ID)
    if (!isMobile || !editorView.dom || !hiddenInput) return
    const onFocus = () => {
      if (!mobileToolbarStore.showDialog) {
        editorView.focus()
      }
    }
    hiddenInput.addEventListener('focus', onFocus)
    return () => hiddenInput.removeEventListener('focus', onFocus)
  })

  // EDI-1763 - Attempted fix to iOS's Chrome/Edge virtual keyboard stuck open while editing a document
  useEffect(() => {
    if (!isMobile || !isMobileIOS || !window.visualViewport) return
    const visualViewport = window.visualViewport
    const toggleEditor = throttle(
      () => {
        if (editorView.hasFocus()) {
          // Force unlock the keyboard in Chrome / Edge (Safari disregards this)
          document.getElementById(HIDDEN_INPUT_ID)?.focus()
          // We only need it to run once
          visualViewport.removeEventListener('resize', toggleEditor)
        }
      },
      300,
      { leading: false, trailing: true },
    )
    visualViewport.addEventListener('resize', toggleEditor)
    return () => visualViewport.removeEventListener('resize', toggleEditor)
  }, [isMobile, isMobileIOS, editorView])
}

function refocusOnTheEditorIfNecessary({
  editorView,
  focusedPm,
}: {
  editorView: EditorView
  focusedPm: Element
}): () => void {
  return throttle(
    () => {
      if (focusedPm === document.activeElement && !editorView.hasFocus()) {
        const selection = window.getSelection()
        if (selection && !selection.anchorNode) {
          editorView.focus()
        }
      }
    },
    100,
    { leading: true, trailing: true },
  )
}

/**
 * scrollThreshold / scrollMargin
 * do not do the magic for the iOS
 * we have to scroll ourselves
 */
function adjustEditorScroll(mainContent: HTMLElement, editorView: EditorView) {
  const mainRect = mainContent.getBoundingClientRect()
  const caretRect = editorView.coordsAtPos(editorView.state.selection.head)

  const visibleArea = mainRect.top + mainRect.height
  // TODO: Fix this the next time the file is edited.
  /* eslint-disable no-nested-ternary */
  const caretOffset =
    caretRect.bottom > visibleArea
      ? // lets scroll down the caret is below
        caretRect.bottom - visibleArea + 50
      : // lets scroll up the caret is up
      caretRect.top < mainRect.top
      ? caretRect.top - mainRect.top - 50
      : 0
  /* eslint-enable no-nested-ternary */

  if (caretOffset) {
    mainContent.scrollTo({
      top: mainContent.scrollTop + caretOffset, // TODO: calculate range rects and move down a certain number of lines
      behavior: 'smooth',
    })
  }
}

function autoScrollToFocused() {
  if (document.activeElement) {
    document.activeElement.scrollIntoViewIfNeeded(true)
  }
}
